import type { AnalyticsAdapter, AnalyticsProps, TrackOptions } from "./AnalyticsAdapter";
import { posthogPrivacyOptions, type PosthogPrivacyOptions } from "./posthogPrivacy";

interface PostHogCaptureOptions {
  send_instantly?: boolean;
}

interface PostHogClient {
  init(key: string, options: { api_host: string } & PosthogPrivacyOptions): void;
  capture(event: string, props?: AnalyticsProps, options?: PostHogCaptureOptions): void;
  identify(distinctId: string): void;
  people: { set(props: AnalyticsProps): void };
  opt_out_capturing?(): void;
  reset?(): void;
}

/**
 * PostHog product-analytics destination. The SDK is loaded with a guarded
 * dynamic import only when init() runs (i.e. only when the env keys are set), so
 * the local build never pulls or executes posthog-js. The client is initialised
 * with privacy-hardened options (see {@link posthogPrivacyOptions}) so no
 * personal data leaves the browser. All calls before/without a loaded client are
 * silent no-ops.
 */
export class PostHogDestination implements AnalyticsAdapter {
  private client: PostHogClient | null = null;
  private readonly key: string;
  private readonly host: string;

  constructor(config: { key: string; host: string }) {
    this.key = config.key;
    this.host = config.host;
  }

  async init(): Promise<void> {
    try {
      const mod = await import("posthog-js");
      const client = (mod.default ?? mod) as unknown as PostHogClient;
      client.init(this.key, { api_host: this.host, ...posthogPrivacyOptions() });
      this.client = client;
    } catch (error) {
      if (typeof console !== "undefined") {
        console.warn("[analytics] posthog init failed", error);
      }
    }
  }

  track(event: string, props?: AnalyticsProps, options?: TrackOptions): void {
    this.client?.capture(event, props, options?.immediate ? { send_instantly: true } : undefined);
  }

  identify(anonId: string): void {
    this.client?.identify(anonId);
  }

  setUserProps(props: AnalyticsProps): void {
    this.client?.people.set(props);
  }

  flush(): void {
    return;
  }

  optOut(): void {
    this.client?.opt_out_capturing?.();
  }

  reset(): void {
    this.client?.reset?.();
  }
}
